import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import type { CustomerDto } from "@finora/types";
import { api } from "../lib/api";

export interface CustomerAutocompleteProps {
  value: string;
  onSelect: (customer: CustomerDto) => void;
  placeholder?: string;
}

export function CustomerAutocomplete({ value, onSelect, placeholder }: CustomerAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [debouncedValue, setDebouncedValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(inputValue), 250);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setInputValue(value);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const { data: customers, isFetching } = useQuery({
    queryKey: ["customers-autocomplete", debouncedValue],
    queryFn: () => api.listCustomers(debouncedValue || undefined),
    enabled: isOpen,
  });

  function handleSelect(customer: CustomerDto) {
    onSelect(customer);
    setInputValue(customer.name);
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="relative block">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={(e) => {
            setIsOpen(true);
            e.target.select();
          }}
          placeholder={placeholder ?? "Kucajte naziv, PIB ili email kupca..."}
          className="h-10 w-full rounded-lg border border-border pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
        />
      </label>

      {isOpen && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-border bg-surface shadow-card">
          {isFetching ? (
            <p className="px-3 py-3 text-sm text-slate-400">Pretraga...</p>
          ) : !customers || customers.length === 0 ? (
            <p className="px-3 py-3 text-sm text-slate-400">Nema kupaca za prikaz.</p>
          ) : (
            <ul>
              {customers.map((customer) => (
                <li key={customer.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(customer)}
                    className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-brand-light"
                  >
                    <span className="font-medium text-slate-800">{customer.name}</span>
                    {customer.taxId && <span className="text-xs text-slate-400">PIB: {customer.taxId}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
